import { createContext, useContext, type PropsWithChildren } from 'react';
import { z } from 'zod';

/** Increment when the renderer/native protocol is no longer backward compatible. */
export const PLATFORM_CONTRACT_VERSION = 1 as const;

export const RuntimeInfoSchema = z.object({
  contractVersion: z.literal(PLATFORM_CONTRACT_VERSION),
  appVersion: z.string(),
  os: z.enum(['windows', 'linux', 'macos', 'web']),
  capabilities: z.object({
    localProjects: z.boolean(),
    readText: z.boolean(),
    writeFiles: z.boolean(),
    centralAgents: z.boolean(),
  }),
});
export type RuntimeInfo = z.infer<typeof RuntimeInfoSchema>;

export const ProjectSchema = z.object({
  id: z.string().min(1),
  name: z.string(),
  rootNodeId: z.string().min(1),
});
export type LocalProject = z.infer<typeof ProjectSchema>;
export const EntrySchema = z.object({
  id: z.string().min(1),
  name: z.string(),
  kind: z.enum(['directory', 'file']),
  size: z.number().nonnegative(),
});
export type ProjectEntry = z.infer<typeof EntrySchema>;
export const DirectorySchema = z.object({
  entries: z.array(EntrySchema),
  truncated: z.boolean(),
  skipped: z.number().int().nonnegative(),
});
export type DirectoryListing = z.infer<typeof DirectorySchema>;
export const TextFileSchema = z.object({ text: z.string(), size: z.number().nonnegative() });
export type TextFile = z.infer<typeof TextFileSchema>;

export class PlatformError extends Error {
  constructor(public readonly code: string, message: string) {
    super(message);
    this.name = 'PlatformError';
  }
}

/** No absolute paths, arbitrary invoke/shell, credentials, or upload API in this interface. */
export interface ProjectGateway {
  open(): Promise<LocalProject | null>;
  list(projectId: string, directoryId: string): Promise<DirectoryListing>;
  readText(projectId: string, fileId: string): Promise<TextFile>;
  close(projectId: string): Promise<void>;
}
export interface PlatformServices {
  readonly kind: 'web' | 'desktop';
  /** Fixed, unprivileged widget document; generated code only crosses postMessage. */
  readonly widgetSandboxUrl?: string;
  runtimeInfo(): Promise<RuntimeInfo>;
  readonly projects: ProjectGateway;
  readonly central?: CentralGateway;
}

export const ServerConfigSchema = z.object({ serverUrl: z.string(), knoxSystemId: z.string() });
export type ServerConfig = z.infer<typeof ServerConfigSchema>;
export const AccessSessionSchema = z.object({ accessToken: z.string().min(1), expiresIn: z.number().positive() });
export type AccessSession = z.infer<typeof AccessSessionSchema>;
export interface CentralGateway {
  /** Read the deployment configuration bundled with the native app. */
  config(): Promise<ServerConfig>;
  check(): Promise<void>;
  login(): Promise<AccessSession>;
  refresh(): Promise<AccessSession | null>;
  clear(): Promise<void>;
}

const PlatformContext = createContext<PlatformServices | null>(null);
export function PlatformProvider({ value, children }: PropsWithChildren<{ value: PlatformServices }>) {
  return <PlatformContext.Provider value={value}>{children}</PlatformContext.Provider>;
}
export function usePlatform(): PlatformServices {
  const platform = useContext(PlatformContext);
  if (!platform) throw new Error('PlatformProvider is required');
  return platform;
}
export function useOptionalPlatform(): PlatformServices | null { return useContext(PlatformContext); }
